import { useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import moment from 'moment';

//* HOC's
import { withUIContext, withLanguageContext } from 'context';

//* Helpers
import { getParamsByKey } from 'helpers/_getParamsByKey';

//* Components
import { Select, DatePicker } from 'antd';
import Icon from 'components/common/Icon';
import Text from 'components/common/Text';

const { RangePicker } = DatePicker;

//* Style
import FilterBarStyle from './style';

//* Config
import { config } from 'helpers';

const FilterBar = ({ queryParamKeys, categories, winWidth, screenSizes, disableDatePicker, onCategoryChange, tags, resetUrl, translate, withOutCatAll = false }) => {
	//! refs
	const wrapRef = useRef();

	//! Router
	const router = useRouter();

	//! Check if query exists
	const queryCheck = Object.keys(router.query).length;

	//! Cat Value
	const catValue = useMemo(() => {
		const catParam = queryParamKeys[1]; //? "catId"
		const activeItems = getParamsByKey(router, catParam);

		return (!isNaN(Number(activeItems?.[0])) ? Number(activeItems?.[0]) : activeItems?.[0]) || null;
	}, [router, queryParamKeys]);

	//! Range value
	const rangeValue = useMemo(() => {
		if (!disableDatePicker) {
			const rangeParam = queryParamKeys[3]; //? "dates"
			const activeItems = getParamsByKey(router, rangeParam);

			return activeItems ? [moment(activeItems[0], 'D/M/Y'), moment(activeItems[1], 'D/M/Y')] : null;
		}
	}, [router, queryParamKeys]);

	//! Store filters
	const storeCategories = useMemo(() => {
		return categories.map((category, index) => {
			return winWidth < screenSizes.desktopSizeXS ? (
				<Select.Option
					key={index}
					value={category.id}>
					{category.name}
				</Select.Option>
			) : (
				<div
					key={index}
					className={`${catValue == category.id ? 'active' : ''} font-ping-medium pS filter-item`}
					role={'button'}
					onClick={() => onCategoryChange('catId', category.id)}>
					{category.name}
				</div>
			);
		});
	}, [categories, catValue, winWidth, screenSizes, onCategoryChange]);

	//! Store tags
	const storeTags = useMemo(() => {
		if (tags) {
			const tagParam = queryParamKeys[2]; //? "tagIds"
			const activeItems = getParamsByKey(router, tagParam);

			return tags.map((tag) => {
				return (
					activeItems &&
					activeItems.includes(String(tag.id)) && (
						<Text
							className={'hashtag font-helvetica pXS'}
							key={tag.id}
							text={tag.name}
							onClick={() => onCategoryChange('tagIds', tag.id, true)}
						/>
					)
				);
			});
		}
	}, [tags, router, queryParamKeys, onCategoryChange]);

	//! Reset filter
	const resetFilter = useCallback(() => {
		if (queryCheck > 0) {
			router.push(resetUrl || config.routes.events.path);
		}
	}, [router, resetUrl]);

	return (
		<FilterBarStyle>
			<div
				className='filter-first-line'
				ref={wrapRef}>
				<div className='category-filters'>
					{winWidth < screenSizes.desktopSizeXS ? (
						<Select
							value={catValue}
							size={'large'}
							suffixIcon={<Icon name={'dropdown'} />}
							onChange={(catId) => onCategoryChange('catId', catId)}>
							<Select.Option
								onClick={() => onCategoryChange('catId', null)}
								value={null}>
								{translate('all')}
							</Select.Option>
							{storeCategories}
						</Select>
					) : (
						<>
							{!withOutCatAll && (
								<div
									className={`${!catValue ? 'active' : ''} font-ping-medium pS filter-item`}
									role={'button'}
									onClick={() => onCategoryChange('catId', null)}>
									{translate('all')}
								</div>
							)}
							{storeCategories}
						</>
					)}
				</div>

				{!disableDatePicker ? (
					<RangePicker
						getPopupContainer={() => wrapRef.current}
						value={rangeValue}
						disabledDate={(current) => current && current < moment().startOf('day') - 1}
						suffixIcon={<Icon name={'calendar'} />}
						className={'range-picker'}
						size={'large'}
						onChange={(d, string) => onCategoryChange('dates', d ? string : d)}
						format={'D/M/Y'}
						inputReadOnly={true}
						placeholder={[translate('startDate'), translate('endDate')]}
					/>
				) : (
					''
				)}
			</div>
			{queryCheck > 0 && (
				<div className='filter-second-line'>
					<Text
						className='reset-filter primaryColor1 underline pXS'
						onClick={resetFilter}
						text={'resetFilters'}
					/>
					<div className='hashtags-wrap'>{storeTags}</div>
				</div>
			)}
		</FilterBarStyle>
	);
};

export default withLanguageContext(withUIContext(FilterBar, ['winWidth', 'screenSizes']), ['translate']);
